import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { z } from 'zod';

import {
	Box,
	Divider,
	Dropdown,
	Form,
	Stack,
	Text,
} from '@ekhodealer/ekho-common/rev';

import type { AiBdcSettings, PersonalityProfile } from '../types';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';

import { useGetAiBdcSettings } from '../api/useGetAiBdcSettings';
import { useUpdateAiBdcSettings } from '../api/useUpdateAiBdcSettings';
import {
	PersonalityDialect,
	PersonalityEmojiUsage,
	PersonalityFollowUpStyle,
	PersonalityFormality,
	PersonalityResponseLength,
	PersonalitySlangUsage,
	PersonalityTone,
	PersonalityTypoSimulation,
} from '../types';

// --- Template presets ---

const TEMPLATE_PRESETS: Record<string, PersonalityProfile> = {
	// --- Industry-specific standards ---
	AUTOMOTIVE_STANDARD: {
		tone: PersonalityTone.PolishedAuthoritative,
		formality: PersonalityFormality.High,
		emojiUsage: PersonalityEmojiUsage.None,
		typoSimulation: PersonalityTypoSimulation.None,
		slangUsage: PersonalitySlangUsage.None,
		dialect: PersonalityDialect.GeneralAmerican,
		responseLength: PersonalityResponseLength.Concise,
		followUpStyle: PersonalityFollowUpStyle.Structured,
	},
	POWERSPORTS_STANDARD: {
		tone: PersonalityTone.UpbeatEnthusiastic,
		formality: PersonalityFormality.Low,
		emojiUsage: PersonalityEmojiUsage.Occasional,
		typoSimulation: PersonalityTypoSimulation.None,
		slangUsage: PersonalitySlangUsage.Light,
		dialect: PersonalityDialect.GeneralAmerican,
		responseLength: PersonalityResponseLength.Moderate,
		followUpStyle: PersonalityFollowUpStyle.Proactive,
	},
	MARINE_STANDARD: {
		tone: PersonalityTone.WarmApproachable,
		formality: PersonalityFormality.Moderate,
		emojiUsage: PersonalityEmojiUsage.None,
		typoSimulation: PersonalityTypoSimulation.None,
		slangUsage: PersonalitySlangUsage.None,
		dialect: PersonalityDialect.GeneralAmerican,
		responseLength: PersonalityResponseLength.Moderate,
		followUpStyle: PersonalityFollowUpStyle.Conversational,
	},
	AGRICULTURAL_STANDARD: {
		tone: PersonalityTone.WarmApproachable,
		formality: PersonalityFormality.Moderate,
		emojiUsage: PersonalityEmojiUsage.None,
		typoSimulation: PersonalityTypoSimulation.None,
		slangUsage: PersonalitySlangUsage.None,
		dialect: PersonalityDialect.GeneralAmerican,
		responseLength: PersonalityResponseLength.Thorough,
		followUpStyle: PersonalityFollowUpStyle.Supportive,
	},
	RV_STANDARD: {
		tone: PersonalityTone.WarmApproachable,
		formality: PersonalityFormality.Moderate,
		emojiUsage: PersonalityEmojiUsage.Occasional,
		typoSimulation: PersonalityTypoSimulation.None,
		slangUsage: PersonalitySlangUsage.None,
		dialect: PersonalityDialect.GeneralAmerican,
		responseLength: PersonalityResponseLength.Thorough,
		followUpStyle: PersonalityFollowUpStyle.Supportive,
	},
	// --- General personality styles ---
	FRIENDLY: {
		tone: PersonalityTone.WarmApproachable,
		formality: PersonalityFormality.Moderate,
		emojiUsage: PersonalityEmojiUsage.Occasional,
		typoSimulation: PersonalityTypoSimulation.None,
		slangUsage: PersonalitySlangUsage.None,
		dialect: PersonalityDialect.GeneralAmerican,
		responseLength: PersonalityResponseLength.Moderate,
		followUpStyle: PersonalityFollowUpStyle.Conversational,
	},
	CASUAL: {
		tone: PersonalityTone.RelaxedConversational,
		formality: PersonalityFormality.Low,
		emojiUsage: PersonalityEmojiUsage.Frequent,
		typoSimulation: PersonalityTypoSimulation.Light,
		slangUsage: PersonalitySlangUsage.Moderate,
		dialect: PersonalityDialect.GeneralAmerican,
		responseLength: PersonalityResponseLength.Short,
		followUpStyle: PersonalityFollowUpStyle.LaidBack,
	},
	ENERGETIC: {
		tone: PersonalityTone.UpbeatEnthusiastic,
		formality: PersonalityFormality.Low,
		emojiUsage: PersonalityEmojiUsage.Frequent,
		typoSimulation: PersonalityTypoSimulation.None,
		slangUsage: PersonalitySlangUsage.Light,
		dialect: PersonalityDialect.GeneralAmerican,
		responseLength: PersonalityResponseLength.Moderate,
		followUpStyle: PersonalityFollowUpStyle.Proactive,
	},
	EMPATHETIC: {
		tone: PersonalityTone.CaringUnderstanding,
		formality: PersonalityFormality.Moderate,
		emojiUsage: PersonalityEmojiUsage.Occasional,
		typoSimulation: PersonalityTypoSimulation.None,
		slangUsage: PersonalitySlangUsage.None,
		dialect: PersonalityDialect.GeneralAmerican,
		responseLength: PersonalityResponseLength.Thorough,
		followUpStyle: PersonalityFollowUpStyle.Supportive,
	},
};

const TEMPLATE_OPTIONS = [
	// Industry standards
	{ id: 'AUTOMOTIVE_STANDARD', label: 'Automotive standard', description: 'Polished, concise, and professional — ideal for automotive sales.' },
	{ id: 'POWERSPORTS_STANDARD', label: 'Powersports standard', description: 'Energetic and enthusiastic — matches the excitement of the riding community.' },
	{ id: 'MARINE_STANDARD', label: 'Marine standard', description: 'Warm and knowledgeable — builds trust with boating buyers.' },
	{ id: 'AGRICULTURAL_STANDARD', label: 'Agricultural standard', description: 'Practical and thorough — speaks the language of equipment buyers.' },
	{ id: 'RV_STANDARD', label: 'RV standard', description: 'Approachable and detailed — guides buyers through a high-consideration purchase.' },
	// General styles
	{ id: 'FRIENDLY', label: 'Friendly', description: 'Warm and approachable. Builds rapport while staying helpful.' },
	{ id: 'CASUAL', label: 'Casual', description: 'Relaxed and conversational. Great for lifestyle-oriented brands.' },
	{ id: 'ENERGETIC', label: 'Energetic', description: 'Upbeat and enthusiastic. Ideal for high-energy sales environments.' },
	{ id: 'EMPATHETIC', label: 'Empathetic', description: 'Caring and understanding. Perfect for guiding first-time buyers.' },
	// Custom
	{ id: 'CUSTOM', label: 'Custom', disabled: true },
];

// --- Dimension dropdown options ---

const TONE_OPTIONS = [
	{ id: PersonalityTone.PolishedAuthoritative, label: 'Polished & authoritative', description: 'Confident, clear, business-like language.' },
	{ id: PersonalityTone.WarmApproachable, label: 'Warm & approachable', description: 'Friendly with professional undertones.' },
	{ id: PersonalityTone.RelaxedConversational, label: 'Relaxed & conversational (Standard)', description: 'Like texting a knowledgeable friend.' },
	{ id: PersonalityTone.UpbeatEnthusiastic, label: 'Upbeat & enthusiastic', description: 'High energy, excitement, exclamation points.' },
	{ id: PersonalityTone.CaringUnderstanding, label: 'Caring & understanding', description: 'Patient, empathetic, and reassuring.' },
];

const FORMALITY_OPTIONS = [
	{ id: PersonalityFormality.High, label: 'High', description: 'Proper grammar, complete sentences, no slang.' },
	{ id: PersonalityFormality.Moderate, label: 'Moderate (Standard)', description: 'Natural language with some informality.' },
	{ id: PersonalityFormality.Low, label: 'Low', description: 'Casual phrasing, contractions, relaxed grammar.' },
];

const EMOJI_OPTIONS = [
	{ id: PersonalityEmojiUsage.None, label: 'None (Standard)', description: 'No emojis in any messages.' },
	{ id: PersonalityEmojiUsage.Occasional, label: 'Occasional', description: 'Light emoji use to add warmth.' },
	{ id: PersonalityEmojiUsage.Frequent, label: 'Frequent', description: 'Regular emoji use throughout conversations.' },
];

const TYPO_OPTIONS = [
	{ id: PersonalityTypoSimulation.None, label: 'None (Standard)', description: 'Perfect spelling and grammar always.' },
	{ id: PersonalityTypoSimulation.Light, label: 'Light', description: 'Occasional minor typos for a human feel.' },
];

const RESPONSE_LENGTH_OPTIONS = [
	{ id: PersonalityResponseLength.Concise, label: 'Concise', description: 'Short, to-the-point responses.' },
	{ id: PersonalityResponseLength.Short, label: 'Short', description: 'Brief but with enough detail to be helpful.' },
	{ id: PersonalityResponseLength.Moderate, label: 'Moderate (Standard)', description: 'Balanced detail and brevity.' },
	{ id: PersonalityResponseLength.Thorough, label: 'Thorough', description: 'Detailed, comprehensive responses.' },
];

const FOLLOW_UP_OPTIONS = [
	{ id: PersonalityFollowUpStyle.Structured, label: 'Structured', description: 'Organized, step-by-step follow-ups.' },
	{ id: PersonalityFollowUpStyle.Conversational, label: 'Conversational (Standard)', description: 'Natural, flowing follow-up messages.' },
	{ id: PersonalityFollowUpStyle.LaidBack, label: 'Laid-back', description: 'Gentle, low-pressure check-ins.' },
	{ id: PersonalityFollowUpStyle.Proactive, label: 'Proactive', description: 'Eager, forward-leaning follow-ups.' },
	{ id: PersonalityFollowUpStyle.Supportive, label: 'Supportive', description: 'Encouraging, patient follow-ups.' },
];

// --- Schema ---

const personalityFormSchema = z.object({
	tone: z.nativeEnum(PersonalityTone),
	formality: z.nativeEnum(PersonalityFormality),
	emojiUsage: z.nativeEnum(PersonalityEmojiUsage),
	typoSimulation: z.nativeEnum(PersonalityTypoSimulation),
	slangUsage: z.nativeEnum(PersonalitySlangUsage),
	dialect: z.nativeEnum(PersonalityDialect),
	responseLength: z.nativeEnum(PersonalityResponseLength),
	followUpStyle: z.nativeEnum(PersonalityFollowUpStyle),
});

type PersonalityFormData = z.infer<typeof personalityFormSchema>;

const DEFAULT_PROFILE = TEMPLATE_PRESETS.AUTOMOTIVE_STANDARD;

// --- Template detection ---

function detectTemplate(values: PersonalityFormData): string {
	for (const [key, preset] of Object.entries(TEMPLATE_PRESETS)) {
		if (
			values.tone === preset.tone &&
			values.formality === preset.formality &&
			values.emojiUsage === preset.emojiUsage &&
			values.typoSimulation === preset.typoSimulation &&
			values.slangUsage === preset.slangUsage &&
			values.dialect === preset.dialect &&
			values.responseLength === preset.responseLength &&
			values.followUpStyle === preset.followUpStyle
		) {
			return key;
		}
	}
	return 'CUSTOM';
}

// --- Template selector (lives inside Form to access form context) ---

function TemplateSelector() {
	const { setValue } = useFormContext<PersonalityFormData>();
	const currentValues = useWatch<PersonalityFormData>();

	const detectedTemplate = useMemo(
		() => detectTemplate(currentValues as PersonalityFormData),
		[currentValues],
	);

	const handleTemplateChange = useCallback(
		(templateKey: string) => {
			const preset = TEMPLATE_PRESETS[templateKey];
			if (!preset) return;
			setValue('tone', preset.tone, { shouldDirty: true });
			setValue('formality', preset.formality, { shouldDirty: true });
			setValue('emojiUsage', preset.emojiUsage, { shouldDirty: true });
			setValue('typoSimulation', preset.typoSimulation, { shouldDirty: true });
			setValue('slangUsage', preset.slangUsage, { shouldDirty: true });
			setValue('dialect', preset.dialect, { shouldDirty: true });
			setValue('responseLength', preset.responseLength, { shouldDirty: true });
			setValue('followUpStyle', preset.followUpStyle, { shouldDirty: true });
		},
		[setValue],
	);

	return (
		<Stack itemsSpacing='4'>
			<Text size='bodySmall' weight='semibold'>Template</Text>
			<Text size='caption' color='secondary'>
				Pick a starting point. You can customize individual dimensions below.
			</Text>
			<Dropdown
				options={TEMPLATE_OPTIONS}
				value={detectedTemplate}
				onSelectionChange={handleTemplateChange}
				placeholder='Select a template'
				size='large'
				buttonVariant='outline'
			/>
		</Stack>
	);
}

// --- Section subheading ---

function DimensionGroup({
	title,
	description,
	children,
}: {
	title: string;
	description: string;
	children: React.ReactNode;
}) {
	return (
		<Stack itemsSpacing='12'>
			<Stack itemsSpacing='4'>
				<Text size='bodySmall' weight='semibold'>{title}</Text>
				<Text size='caption' color='secondary'>{description}</Text>
			</Stack>
			{children}
		</Stack>
	);
}

// --- Defaults ---

function getDefaultValues(settings: AiBdcSettings | undefined): PersonalityFormData {
	const profile = settings?.personalityProfile;
	return {
		tone: profile?.tone ?? DEFAULT_PROFILE.tone,
		formality: profile?.formality ?? DEFAULT_PROFILE.formality,
		emojiUsage: profile?.emojiUsage ?? DEFAULT_PROFILE.emojiUsage,
		typoSimulation: profile?.typoSimulation ?? DEFAULT_PROFILE.typoSimulation,
		slangUsage: profile?.slangUsage ?? DEFAULT_PROFILE.slangUsage,
		dialect: profile?.dialect ?? DEFAULT_PROFILE.dialect,
		responseLength: profile?.responseLength ?? DEFAULT_PROFILE.responseLength,
		followUpStyle: profile?.followUpStyle ?? DEFAULT_PROFILE.followUpStyle,
	};
}

const SLANG_OPTIONS = [
	{ id: PersonalitySlangUsage.None, label: 'None (Standard)', description: 'No slang. Proper, standard language in all messages.' },
	{ id: PersonalitySlangUsage.Light, label: 'Light', description: "Occasional informal expressions like 'awesome' or 'no worries'." },
	{ id: PersonalitySlangUsage.Moderate, label: 'Moderate', description: 'Regular use of common slang and colloquialisms.' },
];

const DIALECT_OPTIONS = [
	{ id: PersonalityDialect.GeneralAmerican, label: 'General American (Standard)', description: 'Neutral, widely understood American English with no regional markers.' },
	{ id: PersonalityDialect.Northeast, label: 'Northeast', description: 'New England flavor — direct, efficient, and no-nonsense.' },
	{ id: PersonalityDialect.NewYorkMidAtlantic, label: 'New York / Mid-Atlantic', description: 'Fast-paced, confident, and straight to the point.' },
	{ id: PersonalityDialect.Southern, label: 'Southern', description: 'Warm, polite, and unhurried — classic Southern hospitality.' },
	{ id: PersonalityDialect.Texan, label: 'Texan', description: "Bold and friendly with a can-do attitude — everything's bigger." },
	{ id: PersonalityDialect.Midwestern, label: 'Midwestern', description: "Genuine, humble, and agreeable — 'ope' and all." },
	{ id: PersonalityDialect.Appalachian, label: 'Appalachian', description: 'Down-to-earth, resourceful, and deeply rooted in community.' },
	{ id: PersonalityDialect.GulfCoastCajun, label: 'Gulf Coast / Cajun', description: 'Lively, colorful, and full of character — laissez les bons temps rouler.' },
	{ id: PersonalityDialect.CaliforniaWestCoast, label: 'California / West Coast', description: 'Laid-back, optimistic, and casually confident.' },
	{ id: PersonalityDialect.PacificNorthwest, label: 'Pacific Northwest', description: 'Thoughtful, eco-conscious, and understated cool.' },
	{ id: PersonalityDialect.Hawaiian, label: 'Hawaiian', description: 'Aloha spirit — warm, inclusive, and easygoing.' },
	{ id: PersonalityDialect.Alaskan, label: 'Alaskan', description: 'Independent, rugged, and straightforward — frontier mentality.' },
	{ id: PersonalityDialect.PuertoRican, label: 'Puerto Rican', description: 'Vibrant, expressive, and community-oriented with bilingual flair.' },
];

// --- Main form ---

function PersonalityProfileForm() {
	const spId = useCurrentServicesProviderId();
	const { data: settings } = useGetAiBdcSettings(spId);
	const updateMutation = useUpdateAiBdcSettings(spId);

	const handleSubmit = async (data: PersonalityFormData): Promise<{ isSuccess: boolean }> => {
		try {
			await updateMutation.mutateAsync({
				personalityProfile: {
					tone: data.tone,
					formality: data.formality,
					emojiUsage: data.emojiUsage,
					typoSimulation: data.typoSimulation,
					slangUsage: data.slangUsage,
					dialect: data.dialect,
					responseLength: data.responseLength,
					followUpStyle: data.followUpStyle,
				},
			});
			return { isSuccess: true };
		} catch {
			return { isSuccess: false };
		}
	};

	return (
		<Box padding='24' background='secondary' rounding='12'>
			<div data-settings-disabled>
				<Form
					name='ai-bdc-personality-profile'
					schema={personalityFormSchema}
					defaultValues={getDefaultValues(settings)}
					onSubmit={handleSubmit}
					title='Personality profile'
					titleLevel='subgroup'
					titleWeight='semibold'
					titleMaxWidth='full'
					description='Define how your AI assistant communicates. Start from a template or customize each dimension to match your brand.'
					tag={{ children: 'Coming soon', size: 'small' }}>
				<Stack itemsSpacing='24'>
					<TemplateSelector />

					<Divider />

					<DimensionGroup
						title='Voice'
						description='Controls the overall voice and level of formality. Tone sets the emotional quality of messages, while formality determines how buttoned-up or relaxed the language feels.'>
						<Form.Field.Dropdown
							schemaField='tone'
							labelText='Tone'
							options={TONE_OPTIONS}
						/>
						<Form.Field.Dropdown
							schemaField='formality'
							labelText='Formality'
							options={FORMALITY_OPTIONS}
						/>
					</DimensionGroup>

					<Divider />

					<DimensionGroup
						title='Expression'
						description='Adjusts how human and expressive messages feel — emoji, typos, and slang each add a layer of personality and relatability.'>
						<Form.Field.Dropdown
							schemaField='emojiUsage'
							labelText='Emoji usage'
							options={EMOJI_OPTIONS}
						/>
						<Form.Field.Dropdown
							schemaField='typoSimulation'
							labelText='Typo simulation'
							options={TYPO_OPTIONS}
						/>
						<Form.Field.Dropdown
							schemaField='slangUsage'
							labelText='Slang'
							options={SLANG_OPTIONS}
						/>
						<Form.Field.Dropdown
							schemaField='dialect'
							labelText='Regional dialect'
							options={DIALECT_OPTIONS}
						/>
					</DimensionGroup>

					<Divider />

					<DimensionGroup
						title='Conversation flow'
						description='Shapes how the AI structures its responses and follows up with leads.'>
						<Form.Field.Dropdown
							schemaField='responseLength'
							labelText='Response length'
							options={RESPONSE_LENGTH_OPTIONS}
						/>
						<Form.Field.Dropdown
							schemaField='followUpStyle'
							labelText='Follow-up style'
							options={FOLLOW_UP_OPTIONS}
						/>
					</DimensionGroup>
				</Stack>
				</Form>
			</div>
		</Box>
	);
}

export function ConversationBehaviorSection() {
	return <PersonalityProfileForm />;
}
